import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { Switch } from "components/ui/switch";
import { Badge } from "components/ui/badge";
import { Progress } from "components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "components/ui/alert";
import { useToast } from "components/ui/use-toast";
import {
  importFromGoogleDrive,
  importFromCsv,
  getImportStatus,
  cancelImport,
  listImportJobs,
} from "../../services/api";
import type { ImportJobSummary } from "../../services/api";
import {
  CloudUpload,
  RefreshCcw,
  History,
  AlertCircle,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";
type ImportErrorDetail = {
  flow?: string;
  question_order?: number;
  media_key?: string | number | null;
  key?: string | number | null;
  question_text?: string | null;
  error_type?: string;
  message: string;
  row_number?: number | null;
  row_index?: number | string | null;
};

type ImportStatus = {
  job_id?: number;
  status?: string;
  started_at?: string;
  updated_at?: string;
  completed_at?: string;
  total_items?: number;
  processed_items?: number;
  skipped_items?: number;
  total_media_files?: number;
  processed_media_files?: number;
  skipped_media_files?: number;
  percentage?: number;
  media_progress_percentage?: number;
  overall_progress_percentage?: number;
  error_count?: number;
  details?: string;
  error?: string;
  error_details?: ImportErrorDetail[] | null;
};

type ImportSource = "drive" | "csv";

const statusVariantMap: Record<string, "default" | "secondary" | "destructive" | "outline"> =
  {
    in_progress: "default",
    completed: "secondary",
    failed: "destructive",
    error: "destructive",
    cancelled: "outline",
    idle: "outline",
  };

const terminalStatuses = new Set(["completed", "failed", "cancelled", "error"]);

const formatDateTime = (value?: string) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const getProgressValue = (progress?: ImportStatus | null) => {
  if (!progress) return 0;
  if (progress.status === "completed") return 100;
  return (
    progress.overall_progress_percentage ??
    progress.percentage ??
    0
  );
};

/** True when media files are being downloaded but question totals aren't known yet. */
const isMediaOnlyPhase = (progress?: ImportStatus | null) => {
  if (!progress) return false;
  return (
    progress.status === "in_progress" &&
    (progress.total_items ?? 0) === 0 &&
    (progress.total_media_files ?? 0) > 0
  );
};

const ContentImport = () => {
  const { addToast } = useToast();

  const [directSource, setDirectSource] = useState<ImportSource>("drive");
  const [driveUrl, setDriveUrl] = useState("");
  const [directCsvFile, setDirectCsvFile] = useState<File | null>(null);
  const [courseName, setCourseName] = useState("");
  const [courseDescription, setCourseDescription] = useState("");
  const [directLearningOutcomes, setDirectLearningOutcomes] = useState<string[]>([]);
  const [replaceMedia, setReplaceMedia] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [currentJobId, setCurrentJobId] = useState<number | null>(null);
  const [progress, setProgress] = useState<ImportStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const lastNotifiedStatusRef = useRef<string | null>(null);

  const [recentJobs, setRecentJobs] = useState<ImportJobSummary[]>([]);

  const refreshRecentJobs = useCallback(async () => {
    try {
      const jobs = await listImportJobs('course');
      setRecentJobs(jobs);
    } catch {
      // silent — non-critical
    }
  }, []);

  useEffect(() => {
    refreshRecentJobs();
  }, [refreshRecentJobs]);

  const pollStatus = useCallback(
    async (jobId: number) => {
      try {
        const statusResponse = await getImportStatus(jobId);

        let derivedStatus = statusResponse.status;
        const hasError = Boolean(statusResponse.error);
        if (hasError && (!derivedStatus || !terminalStatuses.has(derivedStatus))) {
          derivedStatus = "failed";
        }

        const normalizedProgress = derivedStatus
          ? { ...statusResponse, status: derivedStatus }
          : statusResponse;

        setProgress(normalizedProgress);

        const isTerminal = Boolean(
          derivedStatus && terminalStatuses.has(derivedStatus)
        );

        if (isTerminal || hasError) {
          setIsPolling(false);

          const statusForNotification = derivedStatus ?? (hasError ? "failed" : null);
          if (
            statusForNotification &&
            lastNotifiedStatusRef.current !== statusForNotification
          ) {
            if (statusForNotification === "failed" || statusForNotification === "error") {
              addToast({
                title: "Import failed",
                description:
                  statusResponse.error ||
                  statusResponse.details ||
                  "Check the import details for more information.",
                variant: "destructive",
              });
            }

            lastNotifiedStatusRef.current = statusForNotification;
          }
        } else if (derivedStatus && derivedStatus !== lastNotifiedStatusRef.current) {
          lastNotifiedStatusRef.current = derivedStatus;
        }
      } catch (error) {
        console.error("Failed to fetch import status", error);
        addToast({
          title: "Unable to refresh status",
          description: "Polling has been paused. Try again shortly.",
          variant: "destructive",
        });
        setIsPolling(false);
        lastNotifiedStatusRef.current = "error";
      }
    },
    [addToast]
  );

  useEffect(() => {
    if (!currentJobId || !isPolling) return;

    pollStatus(currentJobId);
    const interval = setInterval(() => {
      pollStatus(currentJobId);
    }, 1000);

    return () => clearInterval(interval);
  }, [currentJobId, isPolling, pollStatus]);

  const handleDirectImport = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (directSource === "drive" && !driveUrl.trim()) {
      addToast({
        title: "Drive URL required",
        description: "Please provide the Google Drive CSV folder URL.",
        variant: "destructive",
      });
      return;
    }

    if (directSource === "csv" && !directCsvFile) {
      addToast({
        title: "CSV file required",
        description: "Please select a CSV file to import.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const normalizedLearningOutcomes = directLearningOutcomes
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
      const response = directSource === "drive"
        ? await importFromGoogleDrive({
            drive_url: driveUrl.trim(),
            course_name: courseName.trim() || undefined,
            course_description: courseDescription.trim() || undefined,
            learning_outcomes: normalizedLearningOutcomes.length > 0 ? normalizedLearningOutcomes : undefined,
            replace_existing_media: replaceMedia,
          })
        : await importFromCsv({
            csv_file: directCsvFile as File,
            course_name: courseName.trim() || undefined,
            course_description: courseDescription.trim() || undefined,
            learning_outcomes: normalizedLearningOutcomes.length > 0 ? normalizedLearningOutcomes : undefined,
            replace_existing_media: replaceMedia,
          });
      if (!response?.import_job_id) {
        throw new Error("Unexpected response from server.");
      }

      setCurrentJobId(response.import_job_id);
      setProgress({
        job_id: response.import_job_id,
        status: response.status ?? "in_progress",
        details: response.message ?? "Import started",
        overall_progress_percentage: 0,
        percentage: 0,
      });
      setIsPolling(true);
      lastNotifiedStatusRef.current = "in_progress";
      refreshRecentJobs();

      addToast({
        title: "Import started",
        description:
          "The import is running in the background. You can follow the progress below.",
      });
    } catch (error: any) {
      console.error("Import failed", error);
      addToast({
        title: "Failed to start import",
        description:
          error?.response?.data?.error ||
          error?.message ||
          "Please review the CSV and try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelJob = async () => {
    if (!currentJobId) return;
    setIsCancelling(true);
    try {
      await cancelImport(currentJobId);
      addToast({
        title: "Import cancelled",
        description: "The running import job was cancelled.",
      });
      await pollStatus(currentJobId);
    } catch (error: any) {
      console.error("Failed to cancel import", error);
      addToast({
        title: "Cancellation failed",
        description:
          error?.response?.data?.error ||
          error?.message ||
          "The job may have already finished.",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const derivedStatus = progress?.status ?? "idle";

  const statusBadgeVariant =
    statusVariantMap[derivedStatus] ?? "outline";

  const progressValue = getProgressValue(progress);
  const mediaOnly = isMediaOnlyPhase(progress);

  const metrics = useMemo(
    () => [
      {
        label: "Questions",
        value: progress?.total_items
          ? `${progress?.processed_items ?? 0} / ${progress.total_items}`
          : progress?.status === "completed" ? "0 questions · course structure updated" : "Processing files…",
      },
      {
        label: "Media files",
        value: `${progress?.processed_media_files ?? 0} / ${
          progress?.total_media_files ?? 0
        }`,
      },
      {
        label: "Errors",
        value: progress?.error_count ?? 0,
      },
    ],
    [progress]
  );

  const updateLearningOutcome = useCallback(
    (index: number, value: string) => {
      setDirectLearningOutcomes((prev) => {
        if (index < 0 || index >= prev.length) {
          return prev;
        }
        const next = [...prev];
        next[index] = value;
        return next;
      });
    },
    [],
  );

  const addLearningOutcome = useCallback(() => {
    setDirectLearningOutcomes((prev) => [...prev, ""]);
  }, []);

  const removeLearningOutcome = useCallback((index: number) => {
    setDirectLearningOutcomes((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev;
      }
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  }, []);

  const resizeOutcomeTextarea = useCallback((element: HTMLTextAreaElement | null) => {
    if (!element) {
      return;
    }
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }, []);

  return (
    <div className="space-y-6">
      <CardTitle
        title="Import course"
        subtitle="Import a CSV directly or from Google Drive. Existing modules in the course will be replaced, so review content carefully."
        subtitleSize="big"
      />
      <form onSubmit={handleDirectImport} className="space-y-6 md:w-3/4">
              <div className="space-y-2">
                <Label>Source</Label>
                <Select
                  value={directSource}
                  onValueChange={(value) => setDirectSource(value as ImportSource)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an import source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="drive">Google Drive folder</SelectItem>
                    <SelectItem value="csv">CSV upload</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {directSource === "drive" ? (
                <div className="space-y-2">
                  <Label htmlFor="drive-url">Google Drive folder URL</Label>
                  <Input
                    id="drive-url"
                    placeholder="https://drive.google.com/drive/folders/..."
                    value={driveUrl}
                    onChange={(event) => setDriveUrl(event.target.value)}
                    required
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="direct-csv">CSV file</Label>
                  <Input
                    id="direct-csv"
                    type="file"
                    accept=".csv"
                    className="bg-white/80"
                    onChange={(event) =>
                      setDirectCsvFile(event.target.files?.[0] ?? null)
                    }
                    required
                  />
                  {directCsvFile ? (
                    <p className="text-xs text-muted-foreground">
                      Selected: {directCsvFile.name}
                    </p>
                  ) : null}
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="course-name">
                    Course name override (optional)
                  </Label>
                  <Input
                    id="course-name"
                    placeholder="Life Plan"
                    value={courseName}
                    onChange={(event) => setCourseName(event.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="course-description">
                    Course description (optional)
                  </Label>
                  <Textarea
                    id="course-description"
                    placeholder="One-sentence overview of the imported course..."
                    value={courseDescription}
                    onChange={(event) =>
                      setCourseDescription(event.target.value)
                    }
                    rows={3}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Learning outcomes (optional)</Label>
                <div className="space-y-2">
                  {directLearningOutcomes.map((outcome, index) => (
                    <div key={`direct-outcome-${index}`} className="flex items-start gap-2">
                      <span className="w-6 shrink-0 text-sm text-muted-foreground">{index + 1}.</span>
                      <Textarea
                        value={outcome}
                        onChange={(event) =>
                          updateLearningOutcome(index, event.target.value)
                        }
                        onInput={(event) => resizeOutcomeTextarea(event.currentTarget)}
                        ref={resizeOutcomeTextarea}
                        placeholder="Describe a learning outcome"
                        rows={2}
                        className="min-h-[2.75rem] resize-none overflow-hidden"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLearningOutcome(index)}
                        aria-label={`Remove direct learning outcome ${index + 1}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="secondary"
                    size="auto"
                    onClick={addLearningOutcome}
                    className="inline-flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add outcome
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-border/40 bg-background/40 px-4 py-3">
                <div>
                  <Label className="text-sm">
                    Replace existing media in storage
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    When enabled, any media found in Google Drive replaces files
                    with the same name that already exist in storage.
                  </p>
                </div>
                <Switch
                  checked={replaceMedia}
                  onCheckedChange={setReplaceMedia}
                />
              </div>

              <div className="flex items-center gap-3">
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Starting import…
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CloudUpload className="h-4 w-4" />
                      Start import
                    </span>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={!currentJobId}
                  onClick={() => currentJobId && pollStatus(currentJobId)}
                >
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Refresh status
                </Button>
              </div>
      </form>

      <div className="flex flex-row items-center justify-between gap-4">
        <CardTitle
          title="Import status"
          subtitle="Monitor the most recent import job."
          subtitleSize="small"
        />
        <div className="flex items-center gap-3">
          {recentJobs.length > 0 && (
            <Select
              value={currentJobId?.toString() ?? ""}
              onValueChange={(value) => {
                const jobId = Number(value);
                const job = recentJobs.find((j) => j.id === jobId);
                setCurrentJobId(jobId);
                setProgress(null);
                lastNotifiedStatusRef.current = null;
                if (job && !terminalStatuses.has(job.status)) {
                  setIsPolling(true);
                } else {
                  setIsPolling(false);
                  pollStatus(jobId);
                }
              }}
            >
              <SelectTrigger className="min-w-[17.5rem] max-w-[37.5rem] whitespace-nowrap">
                <SelectValue placeholder="View a previous import…" />
              </SelectTrigger>
              <SelectContent>
                {recentJobs.map((job) => (
                  <SelectItem key={job.id} value={job.id.toString()}>
                    #{job.id} — {job.course_name}{" "}
                    {job.started_at
                      ? new Date(job.started_at).toLocaleDateString()
                      : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Badge variant={statusBadgeVariant} className="shrink-0 whitespace-nowrap">
            Status: {derivedStatus.replace("_", " ")}
          </Badge>
        </div>
      </div>
          {progress ? (
            <>
              <div className="space-y-2">
                <Progress value={progressValue} indeterminate={mediaOnly} />
                <div className="flex flex-wrap justify-between text-xs text-muted-foreground">
                  <span>
                    {mediaOnly
                      ? `Downloading media files… ${progress.processed_media_files ?? 0} / ${progress.total_media_files ?? 0}`
                      : `Overall progress: ${progressValue.toFixed(1)}%`}
                  </span>
                  <span>Job ID: {progress.job_id ?? "-"}</span>
                </div>
              </div>

              {progress.details && (
                <Alert>
                  <AlertTitle>Details</AlertTitle>
                  <AlertDescription>{progress.details}</AlertDescription>
                </Alert>
              )}

              {progress.error && (
                <Alert variant="destructive">
                  <AlertCircle className="mr-2 h-4 w-4" />
                  <AlertTitle>Import reported an error</AlertTitle>
                  <AlertDescription>{progress.error}</AlertDescription>
                </Alert>
              )}

              {Array.isArray(progress.error_details) && progress.error_details.length > 0 && (
                <div className="space-y-3 rounded-2xl border border-destructive/40 bg-destructive/5 p-4 text-sm">
                  <div className="space-y-1">
                    <p className="font-medium text-destructive">Import stopped after detecting unresolved issues.</p>
                    <p className="text-muted-foreground">
                      Resolve the following {progress.error_details.length === 1 ? "issue" : "issues"} and run the import again.
                    </p>
                  </div>
                  <div className="max-h-60 overflow-y-auto rounded-lg border border-border/50 bg-background">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16 text-center">Row</TableHead>
                          <TableHead className="w-24 text-center">Media Key</TableHead>
                          <TableHead className="min-w-[10rem]">Question Key</TableHead>
                          <TableHead>Message</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(progress.error_details ?? []).slice(0, 50).map((issue, index) => (
                          <TableRow key={`${issue.key ?? "question"}-${issue.row_number ?? index}`}>
                            <TableCell className="text-center">
                              {typeof issue.row_number === "number" && !Number.isNaN(issue.row_number)
                                ? issue.row_number
                                : typeof issue.row_index === "number" && !Number.isNaN(issue.row_index)
                                  ? issue.row_index
                                  : issue.row_index ?? "-"}
                            </TableCell>
                            <TableCell className="text-center">
                              {issue.media_key && typeof issue.media_key === "string"
                                ? (issue.media_key.trim().toLowerCase() === "nan" || issue.media_key.trim() === ""
                                    ? "-"
                                    : issue.media_key)
                                : (typeof issue.media_key === "number" && !Number.isNaN(issue.media_key)
                                    ? issue.media_key
                                    : "-")}
                            </TableCell>
                            <TableCell>{issue.key ?? "-"}</TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <p className="font-medium">{issue.message}</p>
                                {issue.question_text ? (
                                  <p className="text-xs text-muted-foreground">
                                    Question: {issue.question_text}
                                  </p>
                                ) : null}
                                {issue.flow ? (
                                  <p className="text-xs text-muted-foreground">
                                    Module: {issue.flow}
                                  </p>
                                ) : null}
                                {typeof issue.question_order === "number" ? (
                                  <p className="text-xs text-muted-foreground">
                                    Question #: {issue.question_order}
                                  </p>
                                ) : null}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {(progress.error_details ?? []).length > 50 ? (
                    <p className="text-xs text-muted-foreground">
                      Showing the first 50 issues out of {(progress.error_details ?? []).length}. Download the CSV to address the rest.
                    </p>
                  ) : null}
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-3">
                {metrics.map((metric) => (
                  <div
                    key={metric.label}
                    className="rounded-2xl border border-border/40 bg-background/40 p-4"
                  >
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      {metric.label}
                    </p>
                    <p className="mt-2 text-lg font-semibold">
                      {metric.value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div className="space-y-1 text-sm">
                  <p className="text-muted-foreground">Started</p>
                  <p className="font-medium">
                    {formatDateTime(progress.started_at)}
                  </p>
                </div>
                <div className="space-y-1 text-sm">
                  <p className="text-muted-foreground">Last updated</p>
                  <p className="font-medium">
                    {formatDateTime(progress.updated_at)}
                  </p>
                </div>
                <div className="space-y-1 text-sm">
                  <p className="text-muted-foreground">Completed</p>
                  <p className="font-medium">
                    {formatDateTime(progress.completed_at)}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  onClick={() => currentJobId && pollStatus(currentJobId)}
                  variant="secondary"
                >
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Refresh now
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={
                    !currentJobId ||
                    !progress ||
                    !progress.status ||
                    terminalStatuses.has(progress.status) ||
                    isCancelling
                  }
                  onClick={handleCancelJob}
                >
                  {isCancelling ? (
                    <span className="flex items-center gap-2 text-destructive">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Cancelling…
                    </span>
                  ) : (
                    <span className="text-destructive">Cancel job</span>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex min-h-[160px] w-full flex-col items-center justify-center gap-1 text-sm text-muted-foreground">
              <History className="h-8 w-8 opacity-60" />
              <p>No import job has been started yet.</p>
            </div>
          )}
    </div>
  );
};

export default ContentImport;

